/**
 * ADO states that count toward row-level **Closed** (every L2 + Eng item must match).
 * **Resolved** is intentionally omitted — resolved work can still be Active or Mitigated (PBI / release rules).
 */
export const ESCALATED_ROW_CLOSED_STATES = new Set([
  'done',
  'closed',
  'completed',
  'removed',
  'cancelled',
  'canceled'
]);

export function isEscalatedWorkItemRowClosedComplete(item) {
  const raw = item?.state;
  if (raw == null || String(raw).trim() === '') return false;
  return ESCALATED_ROW_CLOSED_STATES.has(String(raw).trim().toLowerCase());
}

/** Includes Resolved — for callers that treat Resolved like other finished workflow states (not used for row Closed). */
export const ESCALATED_TERMINAL_STATES = new Set([...ESCALATED_ROW_CLOSED_STATES, 'resolved']);

export function isEscalatedWorkItemTerminal(item) {
  const raw = item?.state;
  if (raw == null || String(raw).trim() === '') return false;
  return ESCALATED_TERMINAL_STATES.has(String(raw).trim().toLowerCase());
}

/** ADO fix_version_dummy values that do not count as an assigned release. */
const MEANINGLESS_RELEASE_VALUES = new Set([
  'tbd',
  'none',
  'n/a',
  'na',
  '-',
  '--',
  'unknown',
  'pending',
  'not set',
  'unset'
]);

export function hasMeaningfulRelease(fixVersionDummy) {
  if (fixVersionDummy == null) return false;
  const s = String(fixVersionDummy).trim();
  if (!s) return false;
  return !MEANINGLESS_RELEASE_VALUES.has(s.toLowerCase());
}

function workItemTypeNorm(item) {
  return String(item?.workItemType || '').trim().toLowerCase();
}

function linkedItemIsPbiOrFeature(item) {
  const w = workItemTypeNorm(item);
  return w === 'product backlog item' || w === 'feature';
}

function linkedItemIsBug(item) {
  return workItemTypeNorm(item) === 'bug';
}

/**
 * Mitigated only when:
 * - L2 links to a Product Backlog Item or Feature, or
 * - L2 links to a Bug that has a real release (fix_version_dummy), not placeholders like None/TBD.
 */
export function rowHasMitigatedSignals(_investigations, linkedBugs) {
  const items = (linkedBugs || []).filter((b) => b != null && b.id != null);
  for (const b of items) {
    if (linkedItemIsPbiOrFeature(b)) return true;
    if (linkedItemIsBug(b) && hasMeaningfulRelease(b.fixVersionDummy)) return true;
  }
  return false;
}

/**
 * Row status: 'Not Applicable' when there is no L2 or Engineering data (unless adminMitigated); else Closed / Mitigated / Active.
 * @param {boolean} [adminMitigated] - Admin set via PATCH; row shows Mitigated until every linked item is row-closed, then Closed.
 * Returns 'Not Applicable' | 'Closed' | 'Active' | 'Mitigated'.
 */
export function getEscalatedRowStatusLabel(investigations, linkedBugs, adminMitigated = false) {
  const invs = (investigations || []).filter((i) => i != null && i.id != null);
  const bugs = (linkedBugs || []).filter((b) => b != null && b.id != null);
  const allItems = [...invs, ...bugs];

  if (allItems.length > 0 && allItems.every(isEscalatedWorkItemRowClosedComplete)) {
    return 'Closed';
  }

  if (invs.length === 0 && bugs.length === 0) {
    if (adminMitigated) return 'Mitigated';
    return 'Not Applicable';
  }

  if (adminMitigated || rowHasMitigatedSignals(investigations, linkedBugs)) {
    return 'Mitigated';
  }

  if (invs.length === 0 && bugs.length > 0) {
    return 'Active';
  }

  if (invs.some((i) => !isEscalatedWorkItemRowClosedComplete(i))) {
    return 'Active';
  }

  if (bugs.some((b) => !isEscalatedWorkItemRowClosedComplete(b))) {
    return 'Active';
  }

  return 'Active';
}

/** True when the row is fully complete (status Closed) — used for bottom section, filters, refresh-all. */
export function isEscalatedRowDone(row) {
  if (row == null) return true;
  const investigations = (row.investigations || []).filter((i) => i != null && i.id != null);
  const linkedBugs = (row.linkedBugs || []).filter((b) => b != null && b.id != null);
  const adminMitigated = row.adminMitigated === true;
  return getEscalatedRowStatusLabel(investigations, linkedBugs, adminMitigated) === 'Closed';
}
